import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import { Edit3, Eraser, Undo2, Redo2, Trash2, X, Save, Check } from 'lucide-react';
import { toast } from 'sonner';

const COLORES = ['#000000', '#FF0000', '#0000FF', '#00FF00', '#FFA500', '#800080'];
const GROSORES = [2, 4, 6, 8];

// URLs de las imágenes originales proporcionadas por el usuario
const GARMENT_IMAGES = {
  camisa: 'https://customer-assets.emergentagent.com/job_ficha-sastreria/artifacts/vwdecibt_camisa.png',
  pantalon: 'https://customer-assets.emergentagent.com/job_ficha-sastreria/artifacts/3jpe7yit_pantalon.png',
  blazer: 'https://customer-assets.emergentagent.com/job_ficha-sastreria/artifacts/6osn9ngp_chaqueta%20y%20blazer.png',
  chaleco: 'https://customer-assets.emergentagent.com/job_ficha-sastreria/artifacts/b79s0kos_chaleco.png',
  chaqueta: 'https://customer-assets.emergentagent.com/job_ficha-sastreria/artifacts/6osn9ngp_chaqueta%20y%20blazer.png'
};

// Mapeo de claves de prendas a sus imágenes correspondientes
const getGarmentImage = (garmentKey) => {
  if (garmentKey.startsWith('camisa')) return GARMENT_IMAGES.camisa;
  if (garmentKey.startsWith('blazer')) return GARMENT_IMAGES.blazer;
  if (garmentKey.startsWith('pantalon')) return GARMENT_IMAGES.pantalon;
  if (garmentKey.startsWith('chaleco')) return GARMENT_IMAGES.chaleco;
  if (garmentKey.startsWith('chaqueta')) return GARMENT_IMAGES.chaqueta;
  return null;
};

const GarmentAnnotationPanel = ({ garmentKey, garmentName, initialAnnotation, onSave }) => {
  const canvasRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [color, setColor] = useState('#FF0000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [mode, setMode] = useState('draw');
  const [isSaving, setIsSaving] = useState(false);
  const [hasAnnotation, setHasAnnotation] = useState(false);

  // Cargar anotaciones existentes cuando se abre el modo edición
  // Soporta formato nuevo (paths) y formato antiguo (data)
  useEffect(() => {
    const pathsStr = initialAnnotation?.paths || initialAnnotation?.data;
    if (pathsStr && canvasRef.current && isEditing) {
      try {
        const pathsData = JSON.parse(pathsStr);
        if (Array.isArray(pathsData) && pathsData.length > 0) {
          setTimeout(() => {
            canvasRef.current.loadPaths(pathsData);
          }, 200);
        }
      } catch (error) {
        console.error('Error loading annotation paths:', error);
      }
    }
  }, [initialAnnotation, isEditing]);

  // Verificar si hay anotación guardada
  // Soporta formato nuevo (paths) y formato antiguo (data)
  useEffect(() => {
    const pathsStr = initialAnnotation?.paths || initialAnnotation?.data;
    if (pathsStr) {
      try {
        const parsed = JSON.parse(pathsStr);
        setHasAnnotation(Array.isArray(parsed) && parsed.length > 0);
      } catch {
        setHasAnnotation(false);
      }
    } else {
      setHasAnnotation(false);
    }
  }, [initialAnnotation]);

  const handleSave = async () => {
    if (!canvasRef.current) return;
    
    setIsSaving(true);
    try {
      // Exportar paths del canvas (para poder recargarlos después)
      const paths = await canvasRef.current.exportPaths();
      
      // Exportar imagen de las anotaciones (solo los trazos, fondo transparente)
      const annotationImage = await canvasRef.current.exportImage('png');
      
      // URL de imagen base
      const baseImageUrl = getGarmentImage(garmentKey);
      
      const annotationData = {
        paths: JSON.stringify(paths),           // Paths para recargar en el canvas
        annotationImage: annotationImage,       // Solo los trazos (transparente)
        garmentImage: baseImageUrl,             // URL de la imagen base
        hasAnnotations: paths.length > 0
      };
      
      onSave(garmentKey, annotationData);
      setHasAnnotation(paths.length > 0);
      setIsEditing(false);
      toast.success('Anotación guardada correctamente');
    } catch (error) {
      console.error('Error saving annotation:', error);
      toast.error('Error al guardar la anotación');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUndo = () => {
    if (canvasRef.current) canvasRef.current.undo();
  };

  const handleRedo = () => {
    if (canvasRef.current) canvasRef.current.redo();
  };

  const handleClear = () => {
    if (window.confirm('¿Limpiar todas las anotaciones de esta silueta?')) {
      if (canvasRef.current) canvasRef.current.clearCanvas();
    }
  };

  const garmentImageUrl = getGarmentImage(garmentKey);

  return (
    <div className="border border-border bg-white overflow-hidden" data-testid={`garment-${garmentKey}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-zinc-50">
        <div className="flex items-center gap-2">
          <h4 className="text-xs uppercase tracking-wider text-zinc-600 font-bold">
            {garmentName}
          </h4>
          {hasAnnotation && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] uppercase tracking-wider">
              <Check className="w-3 h-3" />
              Anotado
            </span>
          )}
        </div>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-3 h-8 bg-black text-white hover:bg-zinc-800 text-[10px] uppercase tracking-wider font-medium transition-all"
            data-testid={`edit-${garmentKey}`}
          >
            <Edit3 className="w-3 h-3" />
            {hasAnnotation ? 'Editar' : 'Anotar'}
          </button>
        )}
      </div>

      {/* Contenido */}
      <div className="relative" style={{ minHeight: '350px' }}>
        {!isEditing ? (
          /* Vista previa - mostrar imagen base */
          <div className="p-4 relative">
            <img 
              src={garmentImageUrl}
              alt={garmentName}
              className="w-full h-auto object-contain"
              style={{ maxHeight: '400px' }}
            />
            {/* Mostrar anotaciones encima si existen (soporta formato nuevo y antiguo) */}
            {hasAnnotation && (initialAnnotation?.annotationImage || initialAnnotation?.image) && (
              <img 
                src={initialAnnotation.annotationImage || initialAnnotation.image}
                alt="Anotaciones"
                className="absolute inset-4 w-[calc(100%-2rem)] h-auto object-contain pointer-events-none"
                style={{ maxHeight: '400px' }}
              />
            )}
          </div>
        ) : (
          /* Modo edición - canvas superpuesto sobre imagen */
          <>
            <div className="relative bg-white" style={{ minHeight: '350px' }}>
              {/* Imagen base de la prenda */}
              <img 
                src={garmentImageUrl}
                alt={garmentName}
                className="w-full h-auto object-contain pointer-events-none select-none"
                style={{ maxHeight: '400px' }}
                draggable={false}
              />
              
              {/* Canvas de dibujo transparente superpuesto */}
              <div className="absolute inset-0" style={{ touchAction: 'none' }}>
                <ReactSketchCanvas
                  ref={canvasRef}
                  strokeColor={color}
                  strokeWidth={strokeWidth}
                  eraserWidth={strokeWidth * 2}
                  canvasColor="transparent"
                  width="100%"
                  height="100%"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    border: 'none',
                    cursor: mode === 'draw' ? 'crosshair' : 'cell'
                  }}
                  data-testid={`canvas-${garmentKey}`}
                />
              </div>
            </div>

            {/* Barra de herramientas */}
            <div className="p-4 border-t border-border bg-zinc-50 space-y-3">
              {/* Herramientas principales */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMode('draw');
                    if (canvasRef.current) canvasRef.current.eraseMode(false);
                  }}
                  className={`px-3 h-8 border text-xs font-medium transition-all ${
                    mode === 'draw'
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-black border-border hover:border-black'
                  }`}
                >
                  Dibujar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode('erase');
                    if (canvasRef.current) canvasRef.current.eraseMode(true);
                  }}
                  className={`px-3 h-8 border text-xs font-medium transition-all flex items-center gap-1 ${
                    mode === 'erase'
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-black border-border hover:border-black'
                  }`}
                >
                  <Eraser className="w-3 h-3" />
                  Borrador
                </button>
                
                <div className="w-px h-6 bg-border mx-1" />
                
                <button 
                  type="button" 
                  onClick={handleUndo} 
                  className="w-8 h-8 border border-border bg-white text-black hover:border-black flex items-center justify-center transition-all"
                  title="Deshacer"
                >
                  <Undo2 className="w-4 h-4" />
                </button>
                <button 
                  type="button" 
                  onClick={handleRedo} 
                  className="w-8 h-8 border border-border bg-white text-black hover:border-black flex items-center justify-center transition-all"
                  title="Rehacer"
                >
                  <Redo2 className="w-4 h-4" />
                </button>
                <button 
                  type="button" 
                  onClick={handleClear} 
                  className="w-8 h-8 border border-border bg-white text-red-600 hover:border-red-600 hover:bg-red-50 flex items-center justify-center transition-all"
                  title="Limpiar todo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Colores */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Color:</span>
                <div className="flex gap-1.5">
                  {COLORES.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`w-7 h-7 rounded-full border-2 transition-all ${
                        color === c ? 'border-black scale-110 shadow-md' : 'border-zinc-300 hover:scale-105'
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
              </div>

              {/* Grosor */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Grosor:</span>
                <div className="flex gap-1">
                  {GROSORES.map(w => (
                    <button
                      key={w}
                      type="button"
                      onClick={() => setStrokeWidth(w)}
                      className={`px-3 h-7 border text-xs font-medium transition-all ${
                        strokeWidth === w
                          ? 'bg-black text-white border-black'
                          : 'bg-white text-black border-border hover:border-black'
                      }`}
                    >
                      {w}px
                    </button>
                  ))}
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 bg-black text-white hover:bg-zinc-800 disabled:bg-zinc-400 h-10 uppercase tracking-wider text-xs font-bold transition-all flex items-center justify-center gap-2"
                  data-testid={`save-${garmentKey}`}
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Guardar Anotación
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-6 bg-white text-black border border-border hover:border-black h-10 uppercase tracking-wider text-xs font-medium transition-all flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const GarmentAnnotationsModule = ({ initialAnnotations, onSave }) => {
  const [annotations, setAnnotations] = useState(initialAnnotations || {});

  // Lista de prendas (como en el Excel original)
  const garments = [
    { key: 'camisa', name: 'Camisa' },
    { key: 'blazer', name: 'Blazer' },
    { key: 'pantalon', name: 'Pantalón' },
    { key: 'chaleco', name: 'Chaleco' },
    { key: 'chaqueta', name: 'Chaqueta / Gabán' }
  ];

  const handleAnnotationSave = useCallback((garmentKey, annotationData) => {
    const updatedAnnotations = {
      ...annotations,
      [garmentKey]: annotationData
    };
    setAnnotations(updatedAnnotations);
    onSave(updatedAnnotations);
  }, [annotations, onSave]);

  return (
    <div className="space-y-6">
      {/* Instrucciones */}
      <div className="bg-amber-50 border border-amber-200 p-4">
        <div className="flex items-start gap-3">
          <Edit3 className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-900 mb-1">
              Anotaciones sobre Siluetas de Prendas
            </p>
            <p className="text-xs text-amber-700 leading-relaxed">
              Haz clic en <strong>"Anotar"</strong> para dibujar directamente sobre las imágenes de las prendas.
              Marca ajustes, detalles y especificaciones. Las anotaciones se fusionan con la imagen
              y aparecerán exactamente igual en el PDF exportado.
            </p>
          </div>
        </div>
      </div>

      {/* Grid de prendas */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {garments.map(({ key, name }) => (
          <GarmentAnnotationPanel
            key={key}
            garmentKey={key}
            garmentName={name}
            initialAnnotation={annotations[key]}
            onSave={handleAnnotationSave}
          />
        ))}
      </div>
    </div>
  );
};
